import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ensureGeistFontFace } from './geist-font';
import { ensureInterFontFace } from './inter-font';
import { ensureStackSansFontFace } from './stack-sans-font';
import tailwindCss from './tailwind.css?inline';
import {
    type ThemeMode,
    applyThemeVars,
    ensureThemeInitialized,
    registerShadowRoot,
    unregisterShadowRoot,
} from './theme';

let tailwindSheet: CSSStyleSheet | null = null;
let tailwindSheetAttempted = false;

const ensureTailwindSheet = (): CSSStyleSheet | null => {
    if (tailwindSheet || tailwindSheetAttempted) {
        return tailwindSheet;
    }
    tailwindSheetAttempted = true;
    if (typeof CSSStyleSheet === 'undefined') {
        return null;
    }
    try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(tailwindCss);
        tailwindSheet = sheet;
    } catch (error) {
        console.error('Failed to construct Tailwind stylesheet:', error);
    }
    return tailwindSheet;
};

const FALLBACK_STYLE_ID = 'malachite-tailwind-style';

const adoptSheet = (root: ShadowRoot, sheet: CSSStyleSheet): boolean => {
    if (!('adoptedStyleSheets' in root)) {
        return false;
    }
    try {
        if (!root.adoptedStyleSheets.includes(sheet)) {
            root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        }
        return true;
    } catch {
        return false;
    }
};

const appendStyleTag = (root: ShadowRoot, css: string): void => {
    if (root.querySelector(`#${FALLBACK_STYLE_ID}`)) {
        return;
    }
    const style = document.createElement('style');
    style.id = FALLBACK_STYLE_ID;
    style.textContent = css;
    root.appendChild(style);
};

// Vite HMR: when tailwind.css changes, mutate the shared sheet in place so every
// already-adopted shadow root picks up the new rules without a full reload.
if (import.meta.hot) {
    import.meta.hot.accept('./tailwind.css?inline', (newModule) => {
        const next = (newModule as unknown as { default?: string } | undefined)?.default;
        if (next && tailwindSheet) {
            tailwindSheet.replaceSync(next);
        }
    });
}

export const ShadowRootWrapper: React.FC<{
    children: React.ReactNode;
    theme?: ThemeMode;
    hostStyle?: React.CSSProperties;
    /** Identity for the shadow host, so host CSS matching unclassed `body` children cannot
     *  blanket-hide a portaled one. Narrow on purpose: a wider type would let a caller
     *  clobber `style`, `ref` or `data-testid` through the spread below. */
    hostProps?: { className?: string } & Record<`data-${string}`, string>;
    /** Reads back the shadow host, which a caller needs to promote it into the top layer. */
    hostRef?: { current: HTMLDivElement | null };
    /** Stretches the portal's inner wrapper div (inside the shadow root) to 100% height, to
     *  match a `hostStyle` height a caller supplies on the host itself — without this, a tall
     *  host div still collapses to content height one level in, because this wrapper div has
     *  no height of its own. Off by default: sizing it for every consumer would change layout
     *  for callers that rely on it hugging its content. */
    fillHeight?: boolean;
}> = ({ children, theme, hostStyle, hostProps, hostRef: externalHostRef, fillHeight = false }) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const takeHost = useCallback(
        (node: HTMLDivElement | null) => {
            hostRef.current = node;
            if (externalHostRef) {
                externalHostRef.current = node;
            }
        },
        [externalHostRef],
    );
    const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

    useEffect(() => {
        if (hostRef.current && !shadowRoot) {
            const shadow =
                hostRef.current.shadowRoot ?? hostRef.current.attachShadow({ mode: 'open' });
            // Adopt the stylesheet before setShadowRoot triggers the render that
            // portals children in: adopting in a later effect leaves a window where
            // the browser can paint the children unstyled (e.g. hover-hidden
            // controls flash fully visible on page load until `opacity: 0` lands).
            const sheet = ensureTailwindSheet();
            if (!sheet || !adoptSheet(shadow, sheet)) {
                appendStyleTag(shadow, tailwindCss);
            }
            setShadowRoot(shadow);
        }
    }, [shadowRoot]);

    useEffect(() => {
        if (!shadowRoot) {
            return;
        }
        // Both of these belong to the document, not the shadow root: @font-face does not
        // load fonts from inside a shadow root, and the theme's custom properties are set on
        // documentElement so light-DOM content and portals resolve them too.
        ensureThemeInitialized();
        ensureInterFontFace();
        ensureGeistFontFace();
        ensureStackSansFontFace();
        if (theme) {
            applyThemeVars(shadowRoot, theme);
            return;
        }
        registerShadowRoot(shadowRoot);
        return () => {
            try {
                unregisterShadowRoot(shadowRoot);
            } catch (error) {
                // Silently handle cleanup errors during hot reloading
                console.warn('Error during shadow root cleanup:', error);
            }
        };
    }, [shadowRoot, theme]);

    if (shadowRoot === null) {
        // Initial render, attach shadow root first
        return (
            <div
                {...hostProps}
                ref={takeHost}
                data-testid="shadow-root-wrapper"
                style={hostStyle}
            />
        );
    }

    // Container div inside the shadow root keeps React context working across the portal.
    return (
        <div {...hostProps} ref={takeHost} style={hostStyle}>
            {shadowRoot &&
                createPortal(
                    <div
                        style={
                            fillHeight
                                ? { isolation: 'isolate', height: '100%' }
                                : { isolation: 'isolate' }
                        }
                    >
                        {children}
                    </div>,
                    shadowRoot,
                )}
        </div>
    );
};
